import {Component, viewChild, signal, inject, DestroyRef, linkedSignal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {StoreClosure} from '../../../../classes/models/store/StoreClosure';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {StoreClosureService} from '../../../../services/http/store/store-closure.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Data required to open the store closure form modal.
 */
export interface StoreClosureFormData {
  storeId: string;
  previousStoreClosure?: StoreClosure;
  weekExists: boolean;
}

/**
 * This component handles creating a new store closure.
 */
@Component({
  selector: 'app-store-closure-form',
  templateUrl: './store-closure-form.component.html',
  styleUrls: ['./store-closure-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class StoreClosureFormComponent {

  private storeClosureService = inject(StoreClosureService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private dataService = inject(DataService);
  private destroyRef = inject(DestroyRef);

  /** A modal component reference for the store closure form. */
  modalComponent = viewChild.required<ModalComponent>('modalComponent');
  /** An identifier of a store. */
  storeId = signal<string>('');
  /** Previous store closure. */
  previousStoreClosure = signal<StoreClosure | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  weekExists = signal<boolean>(false);

  /** A form signal for the date, at which a store is closed. */
  selectedDate = linkedSignal<string | undefined>(() => this.previousStoreClosure()?.startDate);
  /** A form signal for the reason, why a store is closed. */
  selectedReason = linkedSignal<string | undefined>(() => this.previousStoreClosure()?.reason);
  /** Signal that is `true` if request to backend is in progress. */
  loading = signal(false);
  /** The first date from which data can be edited. */
  editDate = signal(this.dataService.editDate);

  static open(modalService: NgbModal, data: StoreClosureFormData): NgbModalRef {
    const modalRef = modalService.open(StoreClosureFormComponent);
    const instance = modalRef.componentInstance as StoreClosureFormComponent;
    instance.storeId.set(data.storeId);
    instance.previousStoreClosure.set(data.previousStoreClosure);
    instance.weekExists.set(data.weekExists);
    return modalRef;
  }

  /**
   * Sends request to backend to create or update a store closure.
   */
  onSubmit(): void {
    this.loading.set(true);

    const storeClosure = new StoreClosure(this.selectedDate(), this.selectedReason());
    if (this.weekExists()) {
      this.editStoreClosure(storeClosure);
    } else {
      this.addStoreClosure(storeClosure);
    }
  }

  private addStoreClosure(storeClosure: StoreClosure): void {
    this.storeClosureService.addStoreClosure(this.storeId(), storeClosure)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store closure was successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }

  private editStoreClosure(storeClosure: StoreClosure): void {
    this.storeClosureService.editStoreClosure(this.storeId(), storeClosure)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent().activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store closure was successfully updated');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}